import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useInvalidateAppQuery } from '@/hooks/useInvalidateAppQuery';
import { api } from '@/lib/api-client';
import type { MutationConfig } from '@/lib/react-query';
import { toaster } from '@/lib/toaster';

import type { PublishSandboxResponse } from '../models/publishSandbox';

interface PublishSandboxParams {
  sandboxId: string;
  gameCode: string;
}

export type { PublishSandboxResponse };

export const publishSandbox = async ({
  sandboxId,
}: PublishSandboxParams): Promise<PublishSandboxResponse> => {
  const response = await api.post<PublishSandboxResponse>(
    `/v1/sandbox/${sandboxId}/publish`,
  );
  return response.data;
};

interface UsePublishSandboxOptions {
  mutationConfig?: MutationConfig<typeof publishSandbox>;
}

export const usePublishSandbox = ({
  mutationConfig,
}: UsePublishSandboxOptions = {}) => {
  const queryClient = useQueryClient();
  const invalidateAppQuery = useInvalidateAppQuery();
  const { onSuccess, ...restConfig } = mutationConfig ?? {};

  return useMutation({
    mutationFn: publishSandbox,
    onSuccess: async (...args) => {
      await queryClient.invalidateQueries({
        queryKey: ['sandbox'],
        refetchType: 'all',
      });
      await invalidateAppQuery({ queryKey: [], refetchType: 'all' });
      toaster.success('Sandbox published successfully');
      onSuccess?.(...args);
    },
    ...restConfig,
  });
};
